import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { generatePath, Link } from "react-router-dom";
import RelativeDate from "../components/RelativeDate";
import API from "../api";
import { useResource } from "../useResource";
import { servicePagePath } from "./ServicePage";
import { Info, Delete, Add } from "@material-ui/icons";
import ConfirmDialog from "../components/ConfirmDialog";
import { Environment, Service } from "../_generated";
import ServiceInitDialog from "./ServiceInitDialog";
import EnvironmentsBulkUpdateDialog from "./EnvironmentsBulkUpdateDialog";
import axios from "axios";
import { BackendURL } from "../globals";
import { authToken } from "../components/Authentication";
import ServiceDeployDialog from "./ServiceDeployDialog";
import DatabaseCreateDialog from "./DatabaseCreateDialog";
import { RdsServiceConfig } from "../rds";
import { ProjectResource } from "../models/ProjectResource";
import analytics from "../analytics";

export default function ServiceListPage(props: { projectName: string }) {
  const [services] = useResource(API.services.list, props.projectName);
  const [environments] = useResource(API.environments.list, props.projectName);
  const [resources, setResources] = useState([] as ProjectResource[]);

  const [confirmID, setConfirmID] = useState(-1);
  const [serviceToDeployName, setServiceToDeployName] = useState("");

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(
    {} as { show: Boolean; servicePk?: Number | undefined }
  );
  const [showServiceDeployDialog, setShowServiceDeployDialog] = useState(false);
  const [showDatabaseCreateDialog, setShowDatabaseCreateDialog] = useState(
    false
  );

  axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

  useEffect(() => {
    analytics.page("ServicesList");
  }, []);

  useEffect(() => {
    if (services.length === 0) {
      setShowCreateDialog(true);
    }
  }, [services]);

  useEffect(() => {
    const url = `${BackendURL}/api/projects/${props.projectName}/resources/`;
    //const url = "https://run.mocky.io/v3/4a2fd42d-d4d6-4a68-97d3-8eafd0a4674e";
    axios.get(url).then((response) => {
      setResources(response.data.results);
    });
  }, [props.projectName]);

  async function onServiceCreate(service: Service) {
    let createdService: Service = await API.services.create(
      props.projectName,
      service
    );
    setShowCreateDialog(false);
    if (environments.length > 0) {
      setShowBulkUpdateDialog({ show: true, servicePk: createdService.pk });
    }
    analytics.track("Service Create Confirmed");
  }

  function onDeleteConfirm(id: number) {
    setConfirmID(-1);
    API.services.delete(String(id), props.projectName);
    analytics.track("Service Delete Confirmed");
  }

  function deployService(service: Service, environment: Environment) {
    axios.post(
      `${BackendURL}/api/projects/${props.projectName}/environments/${environment.pk}/services/${service.pk}/deploy/`
    );
    analytics.track("Service Deploy Confirmed");
  }

  function createDatabase(rdsConfig: RdsServiceConfig) {
    axios.post(`${BackendURL}/api/projects/${props.projectName}/resources/`, {
      resource_type: "RDS_POSTGRES",
      ...rdsConfig,
    });
    analytics.track("Database Create Confirmed");
  }

  function updateEnvironments(
    environments: Environment[],
    servicePk: Number | undefined
  ) {
    environments.forEach((environment) => {
      axios.post(
        `${BackendURL}/api/projects/${props.projectName}/environments/${environment.pk}/applyAndDeploy/`,
        { service_pk: servicePk }
      );
    });
    analytics.track("Environments Update Confirmed");
  }

  function resourceTypeLabel(resource: ProjectResource) {
    if (resource.resource_type === "RDS_POSTGRES") {
      return "Resource: Postgres Database";
    } else {
      return "Resource: " + resource.resource_type.toLowerCase();
    }
  }

  return (
    <>
      <Grid container spacing={2} justify="center" alignItems="stretch">
        {services.map((service) => (
          <Grid item key={service.pk} xs={12} md={6} lg={4}>
            <Link
              to={generatePath(servicePagePath, {
                project_name: props.projectName,
                service_id: String(service.pk),
              })}
              style={{ textDecoration: "none" }}
            >
              <Card variant="outlined">
                <CardContent>
                  <Grid
                    container
                    spacing={0}
                    alignItems="center"
                    justify="space-between"
                  >
                    <Grid item>
                      <Typography variant="h6">
                        {service.alias || service.name}
                        &nbsp;&nbsp;
                        <Chip size="small" label={service.service_type} />
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="caption">
                        <RelativeDate date={service.date_updated} />
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        {service.repository?.replace(/^https?:\/\//, "")}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Grid container alignItems="baseline" justify="space-between">
                    <Grid item>
                      <Button size="medium" variant="text" startIcon={<Info />}>
                        Details
                      </Button>
                    </Grid>
                    <Grid item xs={6} container justify="flex-end">
                      <Grid item>
                        <Button
                          disabled={environments.length === 0}
                          onClick={(e) => {
                            e.preventDefault();
                            setShowServiceDeployDialog(true);
                            setServiceToDeployName(service.name!);
                          }}
                        >
                          Deploy...
                        </Button>
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault();
                            setConfirmID(service.pk!);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardActions>
              </Card>
            </Link>
          </Grid>
        ))}
        <Grid item xs={12} md={6} lg={4} container>
          <Button
            startIcon={<Add />}
            variant="outlined"
            style={{ flexGrow: 1 }}
            onClick={() => {
              setShowCreateDialog(true);
            }}
          >
            Add Resource
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} justify="center" style={{ marginTop: 10 }}>
        {resources.map((resource) => (
          <Grid item xs={6} style={{ minWidth: 300 }}>
            <Card variant="outlined">
              <CardContent>
                <Grid
                  container
                  spacing={0}
                  alignItems="center"
                  justify="space-between"
                >
                  <Grid item>
                    <Typography variant="h6">
                      {resource.name}
                      &nbsp;&nbsp;
                      <Chip size="small" label={resourceTypeLabel(resource)} />
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={8} justify="center" alignItems="stretch">
        <Grid item xs={8}>
          {services.length > 0 && environments.length === 0 && (
            <Alert
              severity="info"
              style={{ marginTop: "10px" }}
              action={
                <Button
                  color="primary"
                  href={`#/projects/${props.projectName}/environments`}
                >
                  Create an environment
                </Button>
              }
            >
              Great, you've got some services! Now create an{" "}
              <strong>environment</strong> to deploy them
            </Alert>
          )}
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmID !== -1}
        id={confirmID}
        onCancel={() => setConfirmID(-1)}
        onConfirm={(id) => onDeleteConfirm(id)}
        text="This will delete the service and remove all deployed components from the cloud. Do you want to proceed?"
      />

      <ServiceInitDialog
        projectName={props.projectName}
        open={showCreateDialog}
        onSubmit={onServiceCreate}
        onChooseDatabase={() => {
          setShowCreateDialog(false);
          setShowDatabaseCreateDialog(true);
        }}
        onCancel={() => setShowCreateDialog(false)}
      />

      <EnvironmentsBulkUpdateDialog
        open={showBulkUpdateDialog.show === true}
        environments={environments}
        servicePk={showBulkUpdateDialog.servicePk}
        onCancel={() => setShowBulkUpdateDialog({ show: false })}
        onConfirm={(environments, servicePk) => {
          setShowBulkUpdateDialog({ show: false });
          updateEnvironments(environments, servicePk);
        }}
      />

      <ServiceDeployDialog
        open={showServiceDeployDialog}
        serviceName={serviceToDeployName}
        environments={environments}
        onCancel={() => setShowServiceDeployDialog(false)}
        onConfirm={(environment) => {
          setShowServiceDeployDialog(false);
          deployService(
            services.find((service) => service.name === serviceToDeployName)!,
            environment
          );
          setServiceToDeployName("");
        }}
      />

      <DatabaseCreateDialog
        open={showDatabaseCreateDialog}
        environments={environments}
        onCancel={() => setShowDatabaseCreateDialog(false)}
        onSubmit={(rdsConfig) => {
          setShowDatabaseCreateDialog(false);
          setShowBulkUpdateDialog({ show: true });
          createDatabase(rdsConfig);
        }}
      />
    </>
  );
}
